import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";

import { continueQuote } from "@covertree/new-business-redux";
import { StateCode } from "@covertree/consts";
import { CountyBlockerErrorType, RedZoneErrorType } from "@covertree/types";
import { Toast } from "@covertree/utils";

import { useAppDispatch } from "@redux/hooks";
import { OVERVIEW_PATH, QUOTES_CONTINUE_FAST_DECLINE } from "@routing/paths";
import { handleCYQRedirection } from "@routing/seedHistoryForCYQ";
import { ContinueQuoteMutation } from "@covertree/agent-portal-graphql";
import { useContinueQuote } from "@providers/ContinueQuoteProvider";
import { PolicyUsage } from "@covertree/new-business-graphql";

const CYQ_ERROR_TOAST_DURATION = 8_000;

const FAST_DECLINE_ERROR_TYPES: ReadonlySet<string> = new Set([
  ...Object.values(RedZoneErrorType),
  ...Object.values(CountyBlockerErrorType),
]);

function isContinueQuoteBlocked(
  error: unknown
): error is { canContinueQuote: boolean } {
  return (
    error !== null && typeof error === "object" && "canContinueQuote" in error
  );
}

function isFastDeclineError(error: { errorType?: string }): boolean {
  return FAST_DECLINE_ERROR_TYPES.has(error.errorType ?? "");
}

export const useContinueQuoteHandler = () => {
  const { showContinueQuoteModal } = useContinueQuote();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const navigateBack = useCallback(() => {
    if (location.state?.previousPath) {
      navigate(-1);
    } else {
      navigate(`/${OVERVIEW_PATH}`, { replace: true });
    }
  }, [navigate, location.state?.previousPath]);

  const handleSuccess = useCallback(
    (continueQuoteResponse: ContinueQuoteMutation["continueQuote"]) => {
      const { policy } = continueQuoteResponse as Extract<
        ContinueQuoteMutation["continueQuote"],
        { policy: unknown }
      >;

      const policyState = policy.units?.[0]?.address?.state;
      const isRental =
        policy.units?.[0]?.usage === PolicyUsage.Rental ? true : false;
      const showSurplusLinesModal = policyState === StateCode.NC;

      handleCYQRedirection(
        navigate,
        policy.step,
        !!policy.bookConversion?.source,
        isRental,
        !!policy.startDateAfterUWReview,
        showSurplusLinesModal
      );
    },
    [navigate]
  );

  const handleError = useCallback(
    (error: unknown, policyLocator: string, effectiveDate?: string) => {
      if (isContinueQuoteBlocked(error)) {
        navigateBack();
        showContinueQuoteModal(policyLocator, effectiveDate, false);
        return;
      }

      const typedError = error as { errorType?: string; message?: string };

      if (isFastDeclineError(typedError)) {
        navigate(`/${QUOTES_CONTINUE_FAST_DECLINE}`, {
          state: {
            previousPath: location.state?.previousPath,
            replace: true,
            errorMessage: typedError.message,
          },
        });
      } else {
        navigateBack();
        Toast.showError((error as Error).message, CYQ_ERROR_TOAST_DURATION);
      }
    },
    [
      navigate,
      navigateBack,
      showContinueQuoteModal,
      location.state?.previousPath,
    ]
  );

  const continueQuoteHandler = useCallback(
    async (policyLocator: string, effectiveDate?: string) => {
      try {
        const response = (await dispatch(
          continueQuote({
            policyLocator,
            ...(effectiveDate ? { effectiveDate } : {}),
          })
        ).unwrap()) as ContinueQuoteMutation["continueQuote"];

        handleSuccess(response);
      } catch (error) {
        handleError(error, policyLocator, effectiveDate);
      }
    },
    [dispatch, handleSuccess, handleError]
  );

  return { continueQuoteHandler };
};
